// format to usd
import { formatInTimeZone } from 'date-fns-tz';

export const capitalizeString = (val: string) =>
  val?.charAt(0).toUpperCase() + val?.slice(1);

type formatCurrencyOptions = {
  currencyIso: string;
  removeCents: boolean;
  notation: 'standard' | 'scientific' | 'engineering' | 'compact' | undefined;
};

export const formatUsd = (
  cents: number | null = 0,
  options?: formatCurrencyOptions,
): string => {
  const { currencyIso, removeCents, notation } = options ?? {
    currencyIso: 'usd',
    removeCents: false,
    notation: 'standard',
  };
  const dollars = cents ? cents / 100 : 0;
  let maximumFractionDigits = 2;
  const maximumCompactFractionDigits = 1;

  if (notation !== 'compact' && removeCents) {
    maximumFractionDigits = 0;
  }

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyIso,
    maximumFractionDigits:
      notation === 'compact'
        ? maximumCompactFractionDigits
        : maximumFractionDigits,
    notation: notation as 'standard' | 'compact',
  });
  return currencyFormatter.format(dollars);
};

export const formatDate = ({
  date,
  includeTime,
  formatString,
}: {
  date: string;
  includeTime?: boolean;
  formatString?: string;
}) => {
  return formatInTimeZone(
    date,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    formatString ?? `M/d/yyyy ${includeTime ? 'h:mm a' : ''}`,
  );
};

export const formatInterestRate = (rate: number): string => {
  return isNumber(rate) ? (rate / 100).toFixed(2) + '%' : '0%';
};

export const getOrdinalNumber = (number: number): string => {
  if (number > 3 && number < 21) {
    return `${number}th`;
  }

  switch (number % 10) {
    case 1:
      return `${number}st`;
    case 2:
      return `${number}nd`;
    case 3:
      return `${number}rd`;
    default:
      return `${number}th`;
  }
};

function isNumber(n: unknown) {
  // @ts-expect-error n may or may not be a number. That's what we're checking
  return !isNaN(parseFloat(n)) && !isNaN(n - 0);
}

export const replaceXWithAsterisk = (val: string) => val.replace(/X/g, '*');

export const toTitleCase = (str: string) => {
  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
  );
};
